/**
 * GX-Header.js 
 *
 * Responsibilities:
 * 1. Enable transparent header only where a full-bleed hero exists.
 * 2. Toggle the scrolled header state.
 * 3. Handle desktop dropdown hover intent.
 * 4. Measure the announcement bar accurately.
 * 5. Lock the mobile drawer to the correct position from the first render.
 */

const GX_SCROLL_THRESHOLD = 40;

class GxHeader {
  constructor() {
    this.header = document.querySelector('.section-header');
    if (!this.header) return;

    this.updateAnnouncementOffset = this.setAnnouncementOffset.bind(this);

    this.setTransparentMode();
    this.setAnnouncementOffset();

    window.requestAnimationFrame(() => {
      this.setAnnouncementOffset();
    });

    this.freezeDrawerPosition();
    this.bindScroll();
    this.bindHoverIntent();

    window.addEventListener('resize', this.updateAnnouncementOffset, {
      passive: true,
    });

    window.visualViewport?.addEventListener(
      'resize',
      this.updateAnnouncementOffset,
      {
        passive: true,
      }
    );

    document.addEventListener(
      'shopify:section:load',
      this.updateAnnouncementOffset
    );

    document.fonts?.ready.then(() => {
      this.setAnnouncementOffset();
    });
  }

  /**
   * Enable the transparent header only when the first section contains a
   * supported full-bleed hero.
   */
  setTransparentMode() {
    const main =
      document.querySelector('#MainContent') ||
      document.querySelector('main');

    if (!main) return;

    const first = main.firstElementChild;
    if (!first) return;

    const hero = first.matches('.video-hero, .gx-full-bleed')
      ? first
      : first.querySelector(
          ':scope > .video-hero, :scope > .gx-full-bleed'
        );

    document.body.classList.toggle('gx-transparent-header', Boolean(hero));
  }

  /**
   * Measure the announcement section using its actual rendered height.
   */
  setAnnouncementOffset() {
    const announcementSection = document.querySelector(
      '.announcement-bar-section'
    );

    const height = announcementSection
      ? announcementSection.getBoundingClientRect().height
      : 0;

    document.documentElement.style.setProperty(
      '--gx-ann-h',
      `${Math.round(height)}px`
    );
  }

  /**
   * Take a fresh height measurement whenever the mobile drawer opens.
   * This prevents the drawer from initially rendering at a stale position.
   */
  freezeDrawerPosition() {
    const detailsEl = document.getElementById(
      'Details-menu-drawer-container'
    );

    const summaryEl = detailsEl?.querySelector(':scope > summary');

    if (!detailsEl || !summaryEl) return;

    const prepareDrawerPosition = () => {
      if (detailsEl.hasAttribute('open')) return;

      const announcementSection = document.querySelector(
        '.announcement-bar-section'
      );

      const height = announcementSection
        ? announcementSection.getBoundingClientRect().height
        : 0;

      const drawerTop = `${Math.round(height)}px`;

      document.documentElement.style.setProperty(
        '--gx-ann-h',
        drawerTop
      );

      document.documentElement.style.setProperty(
        '--gx-drawer-top',
        drawerTop
      );

      /*
       * Force the browser to apply the drawer position before Dawn adds
       * the open state and starts the slide animation.
       */
      document.getElementById('menu-drawer')?.getBoundingClientRect();
    };

    /*
     * pointerdown runs before the native summary click/toggle behaviour,
     * so the drawer receives its final position before its first frame.
     */
    summaryEl.addEventListener('pointerdown', prepareDrawerPosition, {
      passive: true,
    });

    /*
     * Keyboard users can open a summary with Enter or Space.
     */
    summaryEl.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        prepareDrawerPosition();
      }
    });

    /*
     * Toggle is now used only for cleanup after closing.
     * Do not change positioning while the drawer is opening.
     */
detailsEl.addEventListener('toggle', () => {
  if (!detailsEl.hasAttribute('open')) {
    this.setAnnouncementOffset();
  }
});
  }

  /**
   * Toggle the scrolled state without writing styles on every scroll event.
   */
  bindScroll() {
    let ticking = false;

    const update = () => {
      const scrolled = window.scrollY > GX_SCROLL_THRESHOLD;

      document.body.classList.toggle(
        'gx-header-scrolled',
        scrolled
      );

      ticking = false;
    };

    this.onScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', this.onScroll, {
      passive: true,
    });

    update();
  }

  /**
   * Desktop dropdown hover behaviour without interfering with Dawn's click
   * and keyboard handling.
   */
  bindHoverIntent() {
    this.desktop = window.matchMedia('(min-width: 990px)');

    document
      .querySelectorAll('.header__inline-menu > ul > li')
      .forEach((li) => {
        const details = li.querySelector('details');

        if (!details) return;

        li.addEventListener('mouseenter', () => {
          if (!this.desktop.matches) return;

          window.clearTimeout(li.gxCloseTimer);
          details.setAttribute('open', '');
        });

        li.addEventListener('mouseleave', () => {
          if (!this.desktop.matches) return;

          li.gxCloseTimer = window.setTimeout(() => {
            details.removeAttribute('open');
          }, 150);
        });

        details.addEventListener(
          'toggle',
          this.syncMenuOpen.bind(this)
        );
      });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      const open = document.querySelector(
        '.header__inline-menu details[open]'
      );

      if (!open) return;

      open.removeAttribute('open');
      open.querySelector('summary')?.focus();
    });

    document.addEventListener('click', (event) => {
      document
        .querySelectorAll('.header__inline-menu details[open]')
        .forEach((details) => {
          if (!details.contains(event.target)) {
            details.removeAttribute('open');
          }
        });
    });
  }

  /**
   * Keep the header solid whenever a desktop dropdown is open.
   */
  syncMenuOpen() {
    const anyOpen = Boolean(
      document.querySelector(
        '.header__inline-menu details[open]'
      )
    );

    document.body.classList.toggle('gx-menu-open', anyOpen);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => new GxHeader(),
    { once: true }
  );
} else {
  new GxHeader();
}